/**
 * Ticket Status Constants
 *
 * Centralized definitions for ticket statuses. Keep in sync with the Postgres
 * `ticket_status` enum (mirrored by the codegen `Ticket_Status` union).
 *
 * Use these instead of hand-rolling status string arrays in dashboard/report
 * filters — a divergent literal list silently drifts from the real enum (e.g.
 * an "open tickets" filter that forgets `waiting_for_vendor`).
 */

import type { Ticket_Status } from "@/src/gql/graphql";

/**
 * The complete `ticket_status` value set, in UX order (workflow lifecycle).
 * Use for input validation — `z.enum(TICKET_STATUS_VALUES)` — NOT a hand-typed
 * literal array. `satisfies readonly Ticket_Status[]` makes a removed/renamed
 * DB enum value break `tsc` here after codegen.
 */
export const TICKET_STATUS_VALUES = [
  "new",
  "assigned",
  "in_progress",
  "waiting_for_customer",
  "waiting_for_vendor",
  "resolved",
  "closed",
  "cancelled",
] as const satisfies readonly Ticket_Status[];

/** Tickets that still need work / a response — the "open" / "active" set. */
export const ACTIVE_TICKET_STATUSES = [
  "new",
  "assigned",
  "in_progress",
  "waiting_for_customer",
  "waiting_for_vendor",
] as const satisfies readonly Ticket_Status[];

/** Terminal statuses — no further action expected (incl. cancelled/abandoned). */
export const CLOSED_TICKET_STATUSES = [
  "resolved",
  "closed",
  "cancelled",
] as const satisfies readonly Ticket_Status[];

/**
 * Statuses that mean the ticket actually REACHED RESOLUTION (resolved, or
 * resolved-then-closed). Use for resolution-rate / "resolved count" metrics.
 * Deliberately EXCLUDES `cancelled` — a cancelled ticket was abandoned, not
 * resolved (counting `=== "resolved"` alone also undercounts: it drops `closed`).
 */
export const RESOLVED_TICKET_STATUSES = [
  "resolved",
  "closed",
] as const satisfies readonly Ticket_Status[];

export function isActiveTicketStatus(status: Ticket_Status): boolean {
  return (ACTIVE_TICKET_STATUSES as readonly Ticket_Status[]).includes(status);
}

/** True when the ticket reached resolution (resolved or closed). */
export function isResolvedTicketStatus(status: Ticket_Status): boolean {
  return (RESOLVED_TICKET_STATUSES as readonly Ticket_Status[]).includes(status);
}
